// Chat turn orchestration — shared by the full Chat page and the global chat
// dock. Owns the send → stream → mutate-assistant-trace loop over the singleton
// chat store, so both surfaces drive one conversation with identical behaviour.
import { onUnmounted, ref, watch } from 'vue';
import { useChatStore, type TraceBlock, type ChatAttachment } from '../stores/chat';
import { streamChatCompletion, type ChatStreamEvent } from '../api/client';
import { buildDesignerSystem, buildGlobalSystem, designerToolset, type DocKind } from '../designer/chatContext';
import { useChatLive } from './useChatLive';

export type DocContext = { kind: DocKind; id: string } | null;

export function useChatTurn() {
  const store = useChatStore();
  const live = useChatLive();
  const loading = ref(false);
  const error = ref<string | null>(null);
  // Designs surfaced by tool results → "open in Designer" chips.
  const openLinks = ref<Array<{ kind: 'design' | 'dashboard'; id: string }>>([]);
  // The design/dashboard the user is most recently looking at (referenced or
  // rendered in this session) — used to auto-ground questions about its data.
  const currentView = ref<{ kind: 'design' | 'dashboard'; id: string } | null>(null);

  // In-flight stream control. The composable is PER COMPONENT (the /chat page
  // and the dock each own an instance over the shared store), so each surface
  // controls — and aborts — its own turn.
  let controller: AbortController | null = null;
  // Session the in-flight turn is anchored to (captured at send() start).
  let turnSessionId: string | null = null;

  /** Abort the in-flight stream (stop button / session switch / unmount). */
  function cancel(): void {
    if (controller) { controller.abort(); controller = null; }
    loading.value = false;
  }

  // Switching to a DIFFERENT session mid-stream aborts the turn. The lazy
  // ensureSession() inside send() also flips activeId — to the SAME id the
  // turn is anchored to, so it does not self-abort.
  watch(() => store.activeId, (id) => {
    if (loading.value && id !== turnSessionId) cancel();
  });
  // The owning component unmounts → abort its in-flight turn.
  onUnmounted(cancel);

  async function send(text: string, images: ChatAttachment[] = [], docCtx: DocContext = null): Promise<void> {
    if (!text.trim() && !images.length) return;
    error.value = null;

    // Optimistic push FIRST (synchronous, into the thread currently on screen).
    store.pushLocal({ role: 'user', content: text, ...(images.length ? { images } : {}) });

    // Anchor the WHOLE turn to the session active NOW (created lazily if this
    // is the first message). Both persists below target this exact id, so a
    // reply that finishes streaming after the user switched sessions still
    // lands in the thread it belongs to.
    let sessionId: string | null = store.activeId;
    if (!sessionId) {
      try { sessionId = await store.ensureSession(); } catch { /* keep going; thread stays in memory */ }
    }
    turnSessionId = sessionId;
    if (sessionId) {
      try { await store.persist({ role: 'user', content: text, images }, sessionId); } catch { /* keep going; thread stays in memory */ }
    }

    // Live collaboration: in a *shared global* session, mirror this turn to peers
    // (the message now, a "generating…" hint while the model runs, the final
    // answer on completion). Doc-scoped chats are handled by the editor's own
    // live channel, so only the global scope broadcasts here.
    const liveSessionId = !docCtx && sessionId && store.sessions.find((s) => s.id === sessionId)?.visibility === 'shared' ? sessionId : null;
    if (liveSessionId) {
      live.broadcast({
        session_id: liveSessionId, kind: 'message', role: 'user', text,
        ...(images.length ? { images: images.map((i) => ({ mediaType: i.mediaType, dataBase64: i.dataBase64 })) } : {}),
      });
    }

    // Build the messages payload from history. Each previous assistant turn is
    // collapsed to plain text; a user turn with images becomes content blocks.
    const history = store.messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => {
        const msgText = typeof m.content === 'string' ? m.content : extractTextFromTrace(m.content as TraceBlock[]);
        if (m.role === 'user' && m.images?.length) {
          return {
            role: m.role,
            content: [
              ...m.images.map((img) => ({ type: 'image', media_type: img.mediaType, data_base64: img.dataBase64 })),
              ...(msgText ? [{ type: 'text', text: msgText }] : []),
            ] as unknown,
          };
        }
        return { role: m.role, content: msgText as unknown };
      });

    const liveMsg = store.pushLocal({ role: 'assistant', content: [] as TraceBlock[] });
    const blocks = liveMsg.content as TraceBlock[];

    loading.value = true;
    if (liveSessionId) live.broadcast({ session_id: liveSessionId, kind: 'typing', role: 'assistant' });
    const ac = new AbortController();
    controller = ac;
    try {
      let currentText: TraceBlock | null = null;
      // Auto-context: when not bound to a doc for editing, ground the global chat
      // on the design the user most recently looked at (rendered in this session),
      // so "what's the power?" works without naming the id.
      const viewing = !docCtx ? currentView.value : null;
      const systemFields = docCtx
        ? { system: buildDesignerSystem(docCtx.kind, docCtx.id), tools: designerToolset(docCtx.kind) }
        : { system: buildGlobalSystem() + (viewing ? `\n\n## Current view\nThe user is currently looking at the ${viewing.kind} "${viewing.id}". For questions about the data it shows (current values, what a widget reads), call get_design_bindings("${viewing.id}") instead of searching the whole catalog.` : '') };
      await streamChatCompletion(
        // max_tokens omitted → gateway uses the tenant's configured output cap.
        // auto_run_tools: per-session override of the confirm gate (null → tenant default).
        { messages: history, ...systemFields, ...(store.autoRun !== null ? { auto_run_tools: store.autoRun } : {}) },
        (ev) => handleEvent(ev, blocks, (next) => (currentText = next), () => currentText),
        ac.signal,
      );
    } catch (err) {
      // A deliberate cancel (stop / session switch / unmount) is not an error.
      if (!ac.signal.aborted) error.value = err instanceof Error ? err.message : String(err);
    } finally {
      if (controller === ac) controller = null;
      loading.value = false;
      // Persist against the session the turn STARTED in (sessionId), not the
      // one active now — switching sessions mid-stream must not cross-file the
      // reply. A cancelled turn keeps whatever text already streamed.
      const finalText = extractTextFromTrace(blocks);
      if (finalText) {
        if (sessionId) {
          try { await store.persist({ role: 'assistant', content: finalText }, sessionId); } catch { /* ignore */ }
        }
        if (liveSessionId) live.broadcast({ session_id: liveSessionId, kind: 'end', role: 'assistant', text: finalText });
      }
    }
  }

  function handleEvent(
    ev: ChatStreamEvent,
    blocks: TraceBlock[],
    setCurrent: (b: TraceBlock | null) => void,
    getCurrent: () => TraceBlock | null,
  ): void {
    switch (ev.kind) {
      case 'text_delta': {
        let cur = getCurrent();
        if (!cur || cur.type !== 'text') {
          blocks.push({ type: 'text', text: '' });
          cur = blocks[blocks.length - 1]!;
          setCurrent(cur);
        }
        cur.text = (cur.text ?? '') + ev.text;
        break;
      }
      case 'tool_call':
        blocks.push({ type: 'tool_use', tool_call_id: ev.tool_call_id, name: ev.name, input: ev.input });
        setCurrent(null);
        break;
      case 'tool_result': {
        blocks.push({ type: 'tool_result', tool_call_id: ev.tool_call_id, output: ev.content, is_error: ev.is_error });
        try {
          const o = JSON.parse(ev.content) as {
            design_id?: string; dashboard_id?: string;
            url?: string; html?: string; ui_resource?: string; uri?: string; mime_type?: string;
          };
          const found = o.design_id ? { kind: 'design' as const, id: o.design_id } : o.dashboard_id ? { kind: 'dashboard' as const, id: o.dashboard_id } : null;
          if (found && !openLinks.value.some((l) => l.id === found.id)) openLinks.value.push(found);
          if (found) currentView.value = found;
          // The interactive app block is built from the dedicated `ui_resource`
          // event below (ADR 33), not parsed here — that path is plan-gated.
        } catch { /* not JSON */ }
        setCurrent(null);
        break;
      }
      // ADR 33 — MCP Apps: render the tool's interactive UI inline (ChatAppFrame).
      // The server emits this (gated by enable_mcp_apps) right after tool_result.
      case 'ui_resource': {
        const m = ev.resourceUri ? /^ui:\/\/lymon\/(dashboard|design|app)\/(.+)$/.exec(ev.resourceUri) : null;
        const kind = (m ? m[1] : 'design') as 'dashboard' | 'design' | 'app';
        const id = m ? m[2]! : ev.tool_call_id;
        if (ev.url || ev.html) {
          blocks.push({
            type: 'ui_resource',
            app: {
              kind, id,
              ...(ev.resourceUri ? { uri: ev.resourceUri } : {}),
              ...(ev.url ? { url: ev.url } : {}),
              ...(ev.html ? { html: ev.html } : {}),
            },
          });
          // Track the rendered design as the "current view" for auto-context
          // (a design/dashboard the user is now looking at). Apps don't count.
          if (kind === 'design' || kind === 'dashboard') {
            currentView.value = { kind, id };
            if (!openLinks.value.some((l) => l.id === id)) openLinks.value.push({ kind, id });
          }
        }
        break;
      }
      // The assistant proposed a state-changing tool — render a confirm card.
      case 'confirm_request':
        blocks.push({ type: 'confirm', tool_call_id: ev.tool_call_id, name: ev.name, input: ev.input, status: 'pending' });
        break;
      case 'turn_end':
        setCurrent(null);
        break;
      case 'done':
        break;
      case 'error':
        error.value = ev.message;
        break;
    }
  }

  function extractTextFromTrace(blocks: TraceBlock[]): string {
    return blocks.filter((b) => b.type === 'text' && b.text).map((b) => b.text).join('\n');
  }

  return { loading, error, openLinks, currentView, send, cancel };
}
